import React, { useState } from 'react';
import { OnboardingData } from './OnboardingFlow';

interface OnboardingContactProps {
  data: OnboardingData;
  updateData: (updates: Partial<OnboardingData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const OnboardingContact: React.FC<OnboardingContactProps> = ({
  data,
  updateData,
  onNext,
  onBack,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!data.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!data.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!data.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!data.street_address.trim()) {
      newErrors.street_address = 'Address is required';
    }

    if (!data.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!data.zip_code.trim()) {
      newErrors.zip_code = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(data.zip_code)) {
      newErrors.zip_code = 'Please enter a valid ZIP code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Your Information</h2>

      <div style={styles.form}>
        <div style={styles.inputGroup}>
          <label style={styles.label}>Full Name</label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => updateData({ name: e.target.value })}
            style={{
              ...styles.input,
              borderColor: errors.name ? '#E73C3E' : '#e0e0e0',
            }}
            placeholder="Jane Farmer"
            autoFocus
          />
          {errors.name && <span style={styles.error}>{errors.name}</span>}
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>Email</label>
          <input
            type="email"
            value={data.email}
            onChange={(e) => updateData({ email: e.target.value })}
            style={{
              ...styles.input,
              borderColor: errors.email ? '#E73C3E' : '#e0e0e0',
            }}
            placeholder="jane@example.com"
          />
          {errors.email && <span style={styles.error}>{errors.email}</span>}
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>Phone</label>
          <input
            type="tel"
            value={data.phone}
            onChange={(e) => updateData({ phone: formatPhone(e.target.value) })}
            style={{
              ...styles.input,
              borderColor: errors.phone ? '#E73C3E' : '#e0e0e0',
            }}
            placeholder="(509) 555-1234"
          />
          {errors.phone && <span style={styles.error}>{errors.phone}</span>}
        </div>

        <div style={styles.divider} />

        <div style={styles.inputGroup}>
          <label style={styles.label}>Street Address</label>
          <input
            type="text"
            value={data.street_address}
            onChange={(e) => updateData({ street_address: e.target.value })}
            style={{
              ...styles.input,
              borderColor: errors.street_address ? '#E73C3E' : '#e0e0e0',
            }}
            placeholder="123 Farm Road"
          />
          {errors.street_address && <span style={styles.error}>{errors.street_address}</span>}
        </div>

        <div style={styles.row}>
          <div style={{ ...styles.inputGroup, flex: 2 }}>
            <label style={styles.label}>City</label>
            <input
              type="text"
              value={data.city}
              onChange={(e) => updateData({ city: e.target.value })}
              style={{
                ...styles.input,
                borderColor: errors.city ? '#E73C3E' : '#e0e0e0',
              }}
              placeholder="Kettle Falls"
            />
            {errors.city && <span style={styles.error}>{errors.city}</span>}
          </div>

          <div style={{ ...styles.inputGroup, flex: 1 }}>
            <label style={styles.label}>State</label>
            <select
              value={data.state}
              onChange={(e) => updateData({ state: e.target.value })}
              style={styles.select}
            >
              <option value="WA">WA</option>
              <option value="ID">ID</option>
              <option value="MT">MT</option>
              <option value="OR">OR</option>
            </select>
          </div>

          <div style={{ ...styles.inputGroup, flex: 1 }}>
            <label style={styles.label}>ZIP</label>
            <input
              type="text"
              value={data.zip_code}
              onChange={(e) => updateData({ zip_code: e.target.value })}
              style={{
                ...styles.input,
                borderColor: errors.zip_code ? '#E73C3E' : '#e0e0e0',
              }}
              placeholder="99141"
              maxLength={10}
            />
            {errors.zip_code && <span style={styles.error}>{errors.zip_code}</span>}
          </div>
        </div>
      </div>

      <div style={styles.actions}>
        <button style={styles.backButton} onClick={onBack}>
          Back
        </button>
        <button style={styles.nextButton} onClick={handleNext}>
          Continue
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
    fontFamily: "'Open Sans', sans-serif",
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#333',
  },
  input: {
    padding: '0.875rem 1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'border-color 0.2s',
    outline: 'none',
    fontFamily: "'Open Sans', sans-serif",
  },
  select: {
    padding: '0.875rem 1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
  error: {
    fontSize: '0.75rem',
    color: '#E73C3E',
  },
  row: {
    display: 'flex',
    gap: '0.75rem',
  },
  divider: {
    height: '1px',
    backgroundColor: '#e0e0e0',
    margin: '0.5rem 0',
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1rem',
  },
  backButton: {
    flex: 1,
    backgroundColor: 'transparent',
    color: '#666',
    border: '2px solid #e0e0e0',
    padding: '0.875rem 1rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
  nextButton: {
    flex: 2,
    backgroundColor: '#508590',
    color: '#fff',
    border: 'none',
    padding: '0.875rem 1rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
};

export default OnboardingContact;
